import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex gap-2 flex-col" }

import { computed } from "vue";
import { useI18n } from "vue-i18n";
import SelectList from "shared/components/other/OaiListbox.vue";
import { HierarchyTagStore } from "shared/types/HierarchyTag";
import { useCurrentProject, useHasProjectFeature } from "@/composables/project";
import { Report } from "@/types/Report";
import {
  QueryValueAggregation,
  QueryValueFormattingRule,
  QueryValueMetric,
  QueryValueReportConfig,
  QueryValueReportFilters,
} from "@/types/reports/PlotQueryValue";
import ReportFormSection from "@/views/reports/components/ReportFormSection.vue";
import ReportDateFilterField from "@/views/reports/filters/ReportDateFilterField.vue";
import ReportLocationFilterField from "@/views/reports/filters/ReportLocationFilterField.vue";
import ReportProcessFilterField from "@/views/reports/filters/ReportProcessFilterField.vue";
import ReportUnitValueTypeFilterField from "@/views/reports/filters/ReportUnitValueTypeFilterField.vue";
import QueryValueConfigFields from "@/views/reports/plots/query_value/QueryValueConfigFields.vue";
import QueryValueFormattingRules from "@/views/reports/plots/query_value/QueryValueFormattingRules.vue";
import {
  aggregationsByMetric,
  defaultFilters,
  filterKeysByMetric,
  hideAggregationForMetric,
  metrics,
  previousPeriodForByMetric,
} from "@/views/reports/plots/query_value/queryValue";


export default /*#__PURE__*/_defineComponent({
  __name: 'QueryValueForm',
  props: {
    filters: {},
    config: {},
    aggregation: {},
    hierarchyTags: {}
  },
  emits: ["change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();

const currentProject = useCurrentProject();
const workingHoursFeatureEnabled = useHasProjectFeature("working_hours");

const metricOptions = computed(() => {
  const workingHoursFeatureMetrics: QueryValueMetric[] = [
    "working_hours",
    "utilization",
    "unit_value",
  ];
  return metrics
    .map((metric) => ({
      name: t(`analytics.reports.query_value_metrics.${metric}`),
      value: metric,
    }))
    .filter(({ value }) => {
      if (!currentProject.process_groups.includes("default") && value === "unit_value") {
        return false;
      }
      if (workingHoursFeatureMetrics.includes(value) && !workingHoursFeatureEnabled) {
        return false;
      }
      return true;
    });
});

const aggregationOptions = computed(() =>
  aggregationsByMetric[props.config.metric].map((aggregation) => ({
    name: t(`analytics.reports.query_value_aggregations.${aggregation}`),
    value: aggregation,
  })),
);

const filterKeys = computed(() => filterKeysByMetric[props.config.metric]);

const numberAfterFilter = computed(() => (filterKeys.value.length === 0 ? 3 : 4));

const getDefaultFormattingRules = (metric: QueryValueMetric): QueryValueFormattingRule[] => {
  if (metric === "delta_planner") {
    return [
      { value: 0, action: "red_foreground", operator: "greater" },
      { value: 0, action: "green_foreground", operator: "lesser_or_equal" },
    ];
  }
  return [];
};

const handleMetricChange = (metric: QueryValueMetric) => {
  const aggregation = aggregationsByMetric[metric].includes(props.aggregation)
    ? props.aggregation
    : aggregationsByMetric[metric][0];
  const validFilters = Object.fromEntries(
    Object.entries(props.filters).filter(([key]) =>
      filterKeysByMetric[metric].includes(key as keyof QueryValueReportFilters),
    ),
  );
  const showPreviousPeriod = previousPeriodForByMetric[metric].includes(aggregation);
  const config: QueryValueReportConfig = {
    ...props.config,
    metric,
    show_previous_period: showPreviousPeriod,
    formatting_rules: getDefaultFormattingRules(metric),
  };
  emit("change", {
    name: t(`analytics.reports.query_value_metrics.${metric}`),
    config,
    aggregation,
    filters: {
      ...structuredClone(defaultFilters),
      ...validFilters,
    },
  });
};

const handleAggregationChange = (aggregation: QueryValueAggregation) => {
  emit("change", { aggregation });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ReportFormSection, {
      number: "2",
      label: _unref(t)('analytics.reports.metric')
    }, {
      default: _withCtx(() => [
        _createVNode(SelectList, {
          options: metricOptions.value,
          defaultSelected: _ctx.config.metric,
          "onUpdate:selected": handleMetricChange
        }, null, 8, ["options", "defaultSelected"])
      ]),
      _: 1
    }, 8, ["label"]),
    (filterKeys.value.length > 0)
      ? (_openBlock(), _createBlock(ReportFormSection, {
          key: 0,
          number: "3",
          label: _unref(t)('analytics.reports.filters')
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              (filterKeys.value.includes('location'))
                ? (_openBlock(), _createBlock(ReportLocationFilterField, {
                    key: 0,
                    filter: _ctx.filters.location,
                    hierarchyTags: _ctx.hierarchyTags,
                    onChange: _cache[0] || (_cache[0] = ($event: any) => (emit('change', { filters: { ..._ctx.filters, location: $event } })))
                  }, null, 8, ["filter", "hierarchyTags"]))
                : _createCommentVNode("", true),
              (filterKeys.value.includes('processes'))
                ? (_openBlock(), _createBlock(ReportProcessFilterField, {
                    key: 1,
                    filter: _ctx.filters.processes,
                    hierarchyTags: _ctx.hierarchyTags,
                    onChange: _cache[1] || (_cache[1] = ($event: any) => (emit('change', { filters: { ..._ctx.filters, processes: $event } })))
                  }, null, 8, ["filter", "hierarchyTags"]))
                : _createCommentVNode("", true),
              (filterKeys.value.includes('daterange'))
                ? (_openBlock(), _createBlock(ReportDateFilterField, {
                    key: 2,
                    filter: _ctx.filters.daterange,
                    onChange: _cache[2] || (_cache[2] = ($event: any) => (emit('change', { filters: { ..._ctx.filters, daterange: $event } })))
                  }, null, 8, ["filter"]))
                : _createCommentVNode("", true),
              (filterKeys.value.includes('unit_value_type'))
                ? (_openBlock(), _createBlock(ReportUnitValueTypeFilterField, {
                    key: 3,
                    filter: _ctx.filters.unit_value_type,
                    onChange: _cache[3] || (_cache[3] = ($event: any) => (emit('change', { filters: { ..._ctx.filters, unit_value_type: $event } })))
                  }, null, 8, ["filter"]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }, 8, ["label"]))
      : _createCommentVNode("", true),
    (!_unref(hideAggregationForMetric).includes(_ctx.config.metric))
      ? (_openBlock(), _createBlock(ReportFormSection, {
          key: _ctx.config.metric,
          number: numberAfterFilter.value,
          label: _unref(t)('analytics.reports.aggregation'),
          defaultOpen: _ctx.config.metric === 'unit_value'
        }, {
          default: _withCtx(() => [
            _createVNode(SelectList, {
              options: aggregationOptions.value,
              defaultSelected: _ctx.aggregation,
              "onUpdate:selected": handleAggregationChange
            }, null, 8, ["options", "defaultSelected"])
          ]),
          _: 1
        }, 8, ["number", "label", "defaultOpen"]))
      : _createCommentVNode("", true),
    _createVNode(ReportFormSection, {
      number: numberAfterFilter.value + 1,
      label: _unref(t)('analytics.reports.query_value_formatting_rules.header'),
      defaultOpen: false
    }, {
      default: _withCtx(() => [
        _createVNode(QueryValueFormattingRules, {
          metric: _ctx.config.metric,
          rules: _ctx.config.formatting_rules,
          onUpdate: _cache[4] || (_cache[4] = ($event: any) => (emit('change', { config: { ...props.config, formatting_rules: $event } })))
        }, null, 8, ["metric", "rules"])
      ]),
      _: 1
    }, 8, ["number", "label"]),
    _createVNode(ReportFormSection, {
      number: numberAfterFilter.value + 2,
      label: _unref(t)('analytics.reports.plot_config'),
      defaultOpen: false
    }, {
      default: _withCtx(() => [
        _createVNode(QueryValueConfigFields, {
          config: _ctx.config,
          filters: _ctx.filters,
          aggregation: _ctx.aggregation,
          onConfigChange: _cache[5] || (_cache[5] = ($event: any) => (emit('change', { config: $event })))
        }, null, 8, ["config", "filters", "aggregation"])
      ]),
      _: 1
    }, 8, ["number", "label"])
  ], 64))
}
}

})